import axios from 'axios';
import base_url, {urlSearchParams} from '../baseUrls';
import * as allConstants from '../constants/allConstants';
import {inspectionApiTypes} from '../pages/jobs/ipermitJobPages/listViews/inspections/inspectionsGridOptions';
import ApiUtils from './apiUtils';

/** sessionStorage flag: this browser tab was opened from an AI call socket push */
export const AI_CALL_BROWSER_SESSION_KEY = 'ipermitAiCallBrowserSession';

/**
 * localStorage key written when a tab restores availability after the AI-call tab closed, so the
 * other tabs can refresh navbar availability (storage event does not fire in the tab that wrote
 * the value).
 */
export const AI_CALL_AVAILABILITY_RESTORED_KEY = 'ipermitAiCallAvailabilityRestored';

/**
 * localStorage key written when an AI-call inspection prompt is dismissed in one tab
 * so other tabs for the same user close their popup too.
 */
export const AI_CALL_INSPECTION_PROMPT_DISMISSED_KEY = 'ipermitAiCallInspectionPromptDismissed';

/**
 * localStorage key written when an AI-call inspection prompt should open in every tab
 * for the same user (socket may only reach one browser session in production).
 */
export const AI_CALL_INSPECTION_PROMPT_REQUESTED_KEY = 'ipermitAiCallInspectionPromptRequested';

/**
 * localStorage key written when the AI-call tab goes away, asking another tab to end the call.
 *
 * The closing tab cannot end the call itself: `pagehide` fires the same way for a refresh as for
 * a close, and ending the call on a refresh would close it out while the agent is still talking
 * to the homeowner. So the closing tab only records that it went away; a surviving tab waits
 * {@link AI_CALL_TAB_CLOSE_GRACE_MS}, and a refresh withdraws the request before then.
 */
export const AI_CALL_TAB_CLOSED_KEY = 'ipermitAiCallTabClosed';

/**
 * sessionStorage key holding the token of this tab's pending close. sessionStorage survives a
 * refresh but not a close, so finding it again on load is what identifies the refresh.
 */
const AI_CALL_TAB_CLOSED_TOKEN_KEY = 'ipermitAiCallTabClosedToken';

/** How long a surviving tab waits for a refreshed AI-call tab to withdraw the close. */
export const AI_CALL_TAB_CLOSE_GRACE_MS = 10000;

/**
 * A close older than this is ignored by a tab that only finds it on load. By then the agent may
 * well be on a different call, and ending that one would be worse than leaving this one open.
 */
export const AI_CALL_TAB_CLOSED_MAX_AGE_MS = 2 * 60 * 1000;

export const AI_CALL_FROM_QUERY_VALUE = '1';

export const AI_CALL_FROM_AI_QUERY_VALUE = 'true';

export const AI_CALL_INSPECTION_PROMPT_BODY =
  'Please click on open button to edit the inspection and turn off availability';

/**
 * localStorage key written when an incoming AI call should show the accept/decline popup in
 * every tab for the same user (the socket may only reach one browser session in production).
 */
export const AI_CALL_INCOMING_REQUESTED_KEY = 'ipermitAiCallIncomingRequested';

/**
 * localStorage key written when an incoming AI call popup is settled (accepted, declined,
 * expired, or closed by the server) so other tabs for the same user close their popup too.
 */
export const AI_CALL_INCOMING_DISMISSED_KEY = 'ipermitAiCallIncomingDismissed';

export const AI_CALL_INCOMING_PROMPT_BODY = 'Incoming AI call — accept to take it or decline to let it go.';

export const AI_CALL_PASSED_TO_OTHER_AGENT_BODY = 'This call was passed to another agent.';

export function isAiCallBrowserSession() {
  try {
    return sessionStorage.getItem(AI_CALL_BROWSER_SESSION_KEY) === AI_CALL_FROM_QUERY_VALUE;
  } catch {
    return false;
  }
}

export function markAiCallBrowserSession() {
  try {
    sessionStorage.setItem(AI_CALL_BROWSER_SESSION_KEY, AI_CALL_FROM_QUERY_VALUE);
  } catch {
    // ignore quota / private mode
  }
}

export function clearAiCallBrowserSession() {
  try {
    sessionStorage.removeItem(AI_CALL_BROWSER_SESSION_KEY);
  } catch {
    // ignore
  }
}

/** Notifies other browser tabs (e.g. the one that opened this inspection) to set Available again. */
export function notifyParentTabAvailabilityRestored() {
  try {
    localStorage.setItem(AI_CALL_AVAILABILITY_RESTORED_KEY, String(Date.now()));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Called from the AI-call tab's `pagehide`: asks the tabs that are still open to end the call
 * once the grace period passes.
 *
 * @param {object} payload
 * @param {string} [payload.userId]
 * @param {string} [payload.callId] the aiCalls id this tab was on, when it knew it
 */
export function recordAiCallTabClosed(payload) {
  const token = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    sessionStorage.setItem(AI_CALL_TAB_CLOSED_TOKEN_KEY, token);
    localStorage.setItem(
      AI_CALL_TAB_CLOSED_KEY,
      JSON.stringify({
        token,
        userId: payload?.userId != null ? String(payload.userId) : '',
        callId: String(payload?.callId ?? '').trim(),
        at: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Called when an AI-call tab loads: if this tab recorded a close and is still here, it was a
 * refresh, so the close is withdrawn before anyone acts on it.
 */
export function withdrawAiCallTabClosedIfRefreshed() {
  try {
    const token = sessionStorage.getItem(AI_CALL_TAB_CLOSED_TOKEN_KEY);
    if (!token) {
      return;
    }
    sessionStorage.removeItem(AI_CALL_TAB_CLOSED_TOKEN_KEY);
    if (parseAiCallTabClosed(localStorage.getItem(AI_CALL_TAB_CLOSED_KEY))?.token === token) {
      localStorage.removeItem(AI_CALL_TAB_CLOSED_KEY);
    }
  } catch {
    // ignore
  }
}

/** @returns {{ token: string, userId: string, callId: string, at: number } | null} */
export function parseAiCallTabClosed(value) {
  if (value == null || value === '') {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    const token = String(parsed?.token ?? '').trim();
    if (!token) {
      return null;
    }
    return {
      token,
      userId: parsed.userId != null ? String(parsed.userId) : '',
      callId: String(parsed.callId ?? '').trim(),
      at: Number(parsed.at) || 0,
    };
  } catch {
    return null;
  }
}

/** The pending close, if it is still there and still this token (i.e. not withdrawn). */
export function readAiCallTabClosed() {
  try {
    return parseAiCallTabClosed(localStorage.getItem(AI_CALL_TAB_CLOSED_KEY));
  } catch {
    return null;
  }
}

/**
 * Takes the pending close so only one tab acts on it. Not atomic across tabs, but the loser of a
 * race just gets "not on a call" back from `end-my-call`.
 */
export function claimAiCallTabClosed(token) {
  const pending = readAiCallTabClosed();
  if (!pending || pending.token !== token) {
    return false;
  }
  try {
    localStorage.removeItem(AI_CALL_TAB_CLOSED_KEY);
  } catch {
    // ignore
  }
  return true;
}

/**
 * Ends the agent's open call after their AI-call tab closed.
 *
 * `end-my-call` ends whatever call the agent is on, so when the closed tab knew which call it was,
 * check that it is still the open one first — a new call accepted in the meantime must survive.
 *
 * @param {{callId: string}} closed
 * @returns {Promise<'ended' | 'no-call' | 'other-call' | 'error'>} `no-call` means there was
 *   nothing left to end (already ended from the navbar, or another tab got there first);
 *   `other-call` means the agent is on a different call now.
 */
export async function endAgentCallAfterTabClosed(closed) {
  const authToken = await ApiUtils.getAccessToken();
  if (!authToken) {
    return 'error';
  }
  try {
    const open = await axios.get(`${base_url.api}ai/my-open-call`, {
      headers: {Authorization: authToken},
    });
    const openCall = open?.data?.data?.call;
    if (!openCall) {
      return 'no-call';
    }
    if (closed?.callId && openCall.id && openCall.id !== closed.callId) {
      return 'other-call';
    }
    await axios.post(
      `${base_url.api}ai/end-my-call`,
      {},
      {headers: {Authorization: authToken}}
    );
    return 'ended';
  } catch (error) {
    // AI_CALL_NOT_FOUND: another tab got there first, or the call was closed in between.
    return error?.response?.data?.errorCode === 'AI_CALL_NOT_FOUND' ? 'no-call' : 'error';
  }
}

/**
 * Notifies other browser tabs to dismiss the AI-call inspection popup.
 *
 * @param {object} payload
 * @param {string} [payload.userId]
 * @param {string} payload.inspectionJobId
 * @param {'open' | 'close'} payload.action
 */
export function notifyAiCallInspectionPromptDismissed(payload) {
  const inspectionJobId = String(payload?.inspectionJobId ?? '').trim();
  if (!inspectionJobId) {
    return;
  }
  try {
    localStorage.setItem(
      AI_CALL_INSPECTION_PROMPT_DISMISSED_KEY,
      JSON.stringify({
        userId: payload?.userId != null ? String(payload.userId) : '',
        inspectionJobId,
        action: payload?.action === 'open' ? 'open' : 'close',
        at: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

/** @returns {{ userId: string, inspectionJobId: string, action: 'open' | 'close' } | null} */
export function parseAiCallInspectionPromptDismissed(value) {
  if (value == null || value === '') {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const inspectionJobId = String(parsed.inspectionJobId ?? '').trim();
    if (!inspectionJobId) {
      return null;
    }
    return {
      userId: parsed.userId != null ? String(parsed.userId) : '',
      inspectionJobId,
      action: parsed.action === 'open' ? 'open' : 'close',
    };
  } catch {
    return null;
  }
}

/**
 * Notifies other browser tabs to show the AI-call inspection popup.
 *
 * @param {object} payload
 * @param {string} [payload.userId]
 * @param {string} payload.inspectionJobId
 */
export function notifyAiCallInspectionPromptRequested(payload) {
  const inspectionJobId = String(payload?.inspectionJobId ?? '').trim();
  if (!inspectionJobId) {
    return;
  }
  try {
    localStorage.setItem(
      AI_CALL_INSPECTION_PROMPT_REQUESTED_KEY,
      JSON.stringify({
        userId: payload?.userId != null ? String(payload.userId) : '',
        inspectionJobId,
        at: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

/** @returns {{ userId: string, inspectionJobId: string } | null} */
export function parseAiCallInspectionPromptRequested(value) {
  if (value == null || value === '') {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const inspectionJobId = String(parsed.inspectionJobId ?? '').trim();
    if (!inspectionJobId) {
      return null;
    }
    return {
      userId: parsed.userId != null ? String(parsed.userId) : '',
      inspectionJobId,
    };
  } catch {
    return null;
  }
}

/**
 * Notifies other browser tabs to show the incoming AI call accept/decline popup.
 *
 * @param {object} payload
 * @param {string} [payload.userId]
 * @param {{callId: string, expiresAt?: number, inspectionJobId?: string}} payload.call
 */
export function notifyAiCallIncomingRequested(payload) {
  const call = payload?.call;
  const callId = String(call?.callId ?? '').trim();
  if (!callId) {
    return;
  }
  try {
    localStorage.setItem(
      AI_CALL_INCOMING_REQUESTED_KEY,
      JSON.stringify({
        userId: payload?.userId != null ? String(payload.userId) : '',
        call: {
          callId,
          expiresAt: Number(call.expiresAt) || 0,
          inspectionJobId: String(call.inspectionJobId ?? '').trim(),
        },
        at: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

/** @returns {{ userId: string, call: {callId: string, expiresAt: number, inspectionJobId: string} } | null} */
export function parseAiCallIncomingRequested(value) {
  if (value == null || value === '') {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    const call = parsed?.call;
    const callId = String(call?.callId ?? '').trim();
    if (!callId) {
      return null;
    }
    return {
      userId: parsed.userId != null ? String(parsed.userId) : '',
      call: {
        callId,
        expiresAt: Number(call.expiresAt) || 0,
        inspectionJobId: String(call.inspectionJobId ?? '').trim(),
      },
    };
  } catch {
    return null;
  }
}

/**
 * Notifies other browser tabs to close the incoming AI call popup.
 *
 * @param {object} payload
 * @param {string} [payload.userId]
 * @param {string} payload.callId
 */
export function notifyAiCallIncomingDismissed(payload) {
  const callId = String(payload?.callId ?? '').trim();
  if (!callId) {
    return;
  }
  try {
    localStorage.setItem(
      AI_CALL_INCOMING_DISMISSED_KEY,
      JSON.stringify({
        userId: payload?.userId != null ? String(payload.userId) : '',
        callId,
        at: Date.now(),
      })
    );
  } catch {
    // ignore quota / private mode
  }
}

/** @returns {{ userId: string, callId: string } | null} */
export function parseAiCallIncomingDismissed(value) {
  if (value == null || value === '') {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    const callId = String(parsed?.callId ?? '').trim();
    if (!callId) {
      return null;
    }
    return {
      userId: parsed.userId != null ? String(parsed.userId) : '',
      callId,
    };
  } catch {
    return null;
  }
}

function pickCallId(source) {
  if (!source || typeof source !== 'object') {
    return '';
  }
  const raw =
    source.callId ??
    source.call_id ??
    source.assignedCallId ??
    source.callSeq ??
    source.sessionId ??
    source.session_id ??
    source.id;
  if (raw == null || raw === '') {
    return '';
  }
  return String(raw).trim();
}

function pickExpiresAt(source) {
  if (!source || typeof source !== 'object') {
    return 0;
  }
  const raw = source.expiresAt ?? source.expires_at ?? source.expiryAt ?? source.expiry;
  if (raw == null || raw === '') {
    return 0;
  }
  if (typeof raw === 'number') {
    return raw;
  }
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Reads incoming AI call fields from a `data-ready` payload (supports nested shapes).
 * @returns {{callId: string, expiresAt: number, inspectionJobId: string} | null}
 */
export function getIncomingCallFromSocketData(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const sources = [data, data.payload, data.data, data.call];
  const callId = sources.map(pickCallId).find(Boolean) || '';
  if (!callId) {
    return null;
  }
  const expiresAt = sources.map(pickExpiresAt).find((value) => value > 0) || 0;
  const pick = (key) => sources.map((s) => (s && typeof s === 'object' ? s[key] : undefined))
    .find((v) => v !== undefined && v !== null);
  const homeOwner = pick('homeOwner') || {};
  return {
    callId,
    expiresAt,
    /** Identifies this prompt's desktop notification, and answers it from there. */
    answerToken: pick('answerToken') || '',
    inspectionJobId: getInspectionJobIdFromSocketData(data),
    /** The aiCalls document, needed to link an unmatched call to an inspection. */
    aiCallId: pick('aiCallId') || '',
    /** 'inbound' when the homeowner rang us; there is no queued call behind it. */
    direction: pick('direction') || 'outbound',
    /** The AI could not tie this caller to a job, so the agent has to find it. */
    needsInspectionSearch: pick('needsInspectionSearch') === true,
    address: pick('address') || '',
    reason: pick('reason') || '',
    answer: pick('answer') || '',
    summary: pick('summary') || '',
    homeOwnerName: `${homeOwner.firstName || ''} ${homeOwner.lastName || ''}`.trim(),
    homeOwnerPhone: homeOwner.phone || '',
  };
}

function pickInspectionJobId(source) {
  if (!source || typeof source !== 'object') {
    return '';
  }
  const raw =
    source.inspectionJobId ??
    source.inspection_job_id ??
    source.inspection_id ??
    source.inspectionId ??
    source.jobId ??
    source.job_id;
  if (raw == null || raw === '') {
    return '';
  }
  return String(raw).trim();
}

/** Reads inspection record id from a `data-ready` payload (supports nested shapes). */
export function getInspectionJobIdFromSocketData(data) {
  if (!data || typeof data !== 'object') {
    return '';
  }
  return (
    pickInspectionJobId(data) ||
    pickInspectionJobId(data.payload) ||
    pickInspectionJobId(data.data) ||
    pickInspectionJobId(data.inspection) ||
    ''
  );
}

export function buildAiNeedToVerifyInspectionSearchParams() {
  return new URLSearchParams({
    [urlSearchParams.view]: inspectionApiTypes.aiNeedToVerify,
    [urlSearchParams.moduleName]: allConstants.IPERMIT_MODULES.INSPECTION,
    [urlSearchParams.showSameAddressJobs]: 'true',
    [urlSearchParams.openTab]: 'Call Logs',
    [urlSearchParams.fromAiCall]: AI_CALL_FROM_QUERY_VALUE,
    [urlSearchParams.callFromAi]: AI_CALL_FROM_AI_QUERY_VALUE,
  });
}

/** React Router location for the AI "need to verify" inspection + Call Logs tab. */
export function getAiCallInspectionLocation(inspectionJobId) {
  const id = String(inspectionJobId ?? '').trim();
  if (!id) {
    return null;
  }
  return {
    pathname: `${allConstants.PATH.MODULE}/${encodeURIComponent(id)}`,
    search: `?${buildAiNeedToVerifyInspectionSearchParams().toString()}`,
  };
}

export function buildAiNeedToVerifyInspectionUrl(inspectionJobId) {
  const location = getAiCallInspectionLocation(inspectionJobId);
  if (!location) {
    return '';
  }
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${location.pathname}${location.search}`;
}

/**
 * Brings this browser tab/window to the foreground (best-effort).
 * Browsers may block this when the event is not from a user gesture.
 */
export function activateCurrentBrowserWindow() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.focus();
    if (window.top && window.top !== window) {
      window.top.focus();
    }
  } catch {
    // cross-origin iframe or restricted context
  }
}

/**
 * Opens the inspection in a new browser tab.
 * URL: /module/{id}?view=ai+need+to+verify&moduleName=inspection&showSameAddressJobs=true&openTab=Call+Logs&fromAiCall=1&callFromAi=true
 */
export function openAiCallInspectionTab(inspectionJobId) {
  const url = buildAiNeedToVerifyInspectionUrl(inspectionJobId);
  if (!url) {
    return null;
  }
  return window.open(url, '_blank', 'noopener,noreferrer');
}
/**
 * Accepts or declines an incoming AI call.
 * POST /api/ai/call/confirm { callId, confirmed }
 * A 409 means the call was already handed to another agent.
 *
 * @param {object} args
 * @param {string} args.callId
 * @param {boolean} args.confirmed
 * @returns {Promise<{ok: boolean, passedToAnotherAgent?: boolean, error?: unknown}>}
 */
export async function confirmAiCall({callId, confirmed}) {
  const authToken = await ApiUtils.getAccessToken();
  if (!authToken) {
    return {ok: false};
  }
  try {
    await axios.post(
      `${base_url.api}ai/call/confirm`,
      {callId, confirmed: Boolean(confirmed)},
      {headers: {Authorization: authToken}}
    );
    return {ok: true};
  } catch (error) {
    if (error?.response?.status === 409) {
      return {ok: false, passedToAnotherAgent: true};
    }
    return {ok: false, error};
  }
}

/**
 * The incoming-call prompt this agent still has open, if any.
 *
 * `ai-call-incoming` is a fire-and-forget socket emit, so a portal that was reconnecting when the
 * call was handed over never sees it and the agent misses the call without knowing one came in.
 * Asking on every connect turns that into a prompt that shows up a moment late.
 *
 * @returns {Promise<object|null>} the same payload the socket event carries, or null.
 */
export async function fetchPendingAiCall() {
  const authToken = await ApiUtils.getAccessToken();
  if (!authToken) {
    return null;
  }
  try {
    const response = await axios.get(`${base_url.api}ai/call/pending`, {
      headers: {Authorization: authToken},
    });
    return response?.data?.data?.call ?? null;
  } catch {
    // Nothing to recover; the socket is still the primary path.
    return null;
  }
}

/** Restore agent availability (same endpoint as navbar "Available" toggle). */
export async function setAgentAvailableAgain() {

  console.log('inside setAgentAvailableAgain');
  
  const authToken = await ApiUtils.getAccessToken();
  if (!authToken) {
    return false;
  }
  await axios.post(
    `${base_url.api}users/update-call-agent`,
    {aiAvailable: true},
    {headers: {Authorization: authToken}}
  );
  return true;
}

/**
 * Inspections matching a job number or part of an address, for attaching an unmatched call.
 * Searches every inspection, not only AI-enabled ones - an inbound caller has no reason to be
 * on an AI-enabled job.
 */
export async function searchInspectionsForCall(query) {
  const authToken = await ApiUtils.getAccessToken();
  if (!authToken) {
    return [];
  }
  try {
    const response = await axios.post(
      `${base_url.api}ai/search-inspections-for-call`,
      {query},
      {headers: {Authorization: authToken}}
    );
    const list = response?.data?.data?.inspections;
    return Array.isArray(list) ? list : [];
  } catch (error) {
    return [];
  }
}

/** Attaches a call that arrived with no job to the inspection the agent picked. */
export async function linkCallToInspection({aiCallId, jobNumber}) {
  const authToken = await ApiUtils.getAccessToken();
  if (!authToken) {
    return {ok: false};
  }
  try {
    await axios.post(
      `${base_url.api}ai/link-call-to-inspection`,
      {aiCallId, jobNumber},
      {headers: {Authorization: authToken}}
    );
    return {ok: true};
  } catch (error) {
    return {ok: false, error: error?.response?.data?.message || 'Could not link this call.'};
  }
}
